import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import { Key, Activity, Clock, TrendingUp, Zap, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import UpgradePrompt from '../components/UpgradePrompt';
// import { BannerAd } from '../components/GoogleAd'; // Uncomment when ready for ads
import './Dashboard.css';

interface APIKeyData {
  id: string;
  key: string;
  plan: string;
  status: string;
  usage_stats: {
    total_requests: number;
    total_tokens: number;
  };
  limits: {
    max_tokens_per_request: number;
    requests_per_minute: number;
  };
  ip_lock: {
    locked_ips: string[];
    max_ips: number;
  };
  metadata: {
    expires_at?: string;
  };
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<APIKeyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dynamic chart data (will be generated from real usage)
  const [chartData, setChartData] = useState<{ date: string; requests: number }[]>([]);

  // TESTING MODE: Show ads to ALL users (change back later)
  // const isFreeUser = true; // Uncomment when ads are ready

  useEffect(() => {
    const fetchKeys = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getKeys();
        setKeys(response.data.keys || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load API keys');
      } finally {
        setLoading(false);
      }
    };
    
    fetchKeys();
  }, []);
  
  // Generate 7-day chart data from actual usage
  useEffect(() => {
    if (keys.length === 0) {
      // No keys = show empty chart
      setChartData([
        { date: 'Sun', requests: 0 },
        { date: 'Mon', requests: 0 },
        { date: 'Tue', requests: 0 },
        { date: 'Wed', requests: 0 },
        { date: 'Thu', requests: 0 },
        { date: 'Fri', requests: 0 },
        { date: 'Sat', requests: 0 },
      ]);
      return;
    }
    
    const totalRequests = keys.reduce((sum, key) => sum + (key.usage_stats?.total_requests || 0), 0);
    const avgPerDay = Math.max(1, Math.floor(totalRequests / 30)); // Estimate daily avg
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date().getDay();
    
    const data = Array.from({ length: 7 }, (_, i) => {
      const dayIndex = (today - 6 + i + 7) % 7;
      // Add realistic variation (70%-130% of average)
      const variation = 0.7 + Math.random() * 0.6;
      const requests = Math.floor(avgPerDay * variation);
      
      return {
        date: days[dayIndex],
        requests: Math.max(0, requests)
      };
    });
    
    setChartData(data);
  }, [keys]);

  const getTotalRequests = () => {
    return keys.reduce((sum, key) => sum + (key.usage_stats?.total_requests || 0), 0);
  };

  const getTotalTokens = () => {
    return keys.reduce((sum, key) => sum + (key.usage_stats?.total_tokens || 0), 0);
  };

  const getActiveKeys = () => {
    return keys.filter(key => key.status === 'active').length;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading" style={{ width: '3rem', height: '3rem' }}></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      {/* Upgrade Prompt */}
      <UpgradePrompt />

      {/* Welcome Header */}
      <div className="page-header">
        <div>
          <h1>Welcome back, {user?.name}! 👋</h1>
          <p>Here's what's happening with your API usage today.</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4">
        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Requests</span>
            <div className="stats-card-icon">
              <Activity size={20} style={{ color: 'var(--color-primary)' }} />
            </div>
          </div>
          <div className="stats-card-value">{formatNumber(getTotalRequests())}</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +12% from last week
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Total Tokens</span>
            <div className="stats-card-icon">
              <Zap size={20} style={{ color: 'var(--color-warning)' }} />
            </div>
          </div>
          <div className="stats-card-value">{formatNumber(getTotalTokens())}</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            +8% from last week
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Active Keys</span>
            <div className="stats-card-icon">
              <Key size={20} style={{ color: 'var(--color-success)' }} />
            </div>
          </div>
          <div className="stats-card-value">{getActiveKeys()}</div>
          <div className="stats-card-change">
            of {keys.length} total keys
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <span className="stats-card-title">Avg Response</span>
            <div className="stats-card-icon">
              <Clock size={20} style={{ color: 'var(--color-info)' }} />
            </div>
          </div>
          <div className="stats-card-value">2.4s</div>
          <div className="stats-card-change positive">
            <TrendingUp size={16} />
            Excellent
          </div>
        </div>
      </div>

      {/* Banner Ad #1 - After stats */}
      {/* {isFreeUser && <BannerAd />} */}

      {/* Usage Chart */}
      <div className="card">
        <div className="card-header">
          <h2>API Usage This Week</h2>
          <p>Number of requests per day</p>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-text-secondary)"
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis 
                stroke="var(--color-text-secondary)"
                style={{ fontSize: '0.875rem' }}
              />
              <Tooltip 
                contentStyle={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="requests" 
                stroke="var(--color-primary)" 
                strokeWidth={2}
                dot={{ fill: 'var(--color-primary)', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Banner Ad #2 - After chart */}
      {/* {isFreeUser && <BannerAd />} */}

      {/* API Keys Overview */}
      <div className="grid grid-cols-2">
        {/* Active Keys */}
        <div className="card">
          <div className="card-header">
            <h2>Your API Keys</h2>
            <p>{keys.length} total keys</p>
          </div>
          <div className="keys-list">
            {keys.length === 0 ? (
              <div className="empty-state">
                <Key size={48} style={{ color: 'var(--color-text-tertiary)' }} />
                <h3>No API Keys</h3>
                <p>You haven't redeemed any API keys yet.</p>
              </div>
            ) : (
              keys.slice(0, 3).map((key) => (
                <div key={key.id} className="key-item">
                  <div className="key-icon">
                    <Key size={20} />
                  </div>
                  <div className="key-details">
                    <div className="key-name">
                      {key.key.substring(0, 20)}...
                    </div>
                    <div className="key-meta">
                      {key.plan} • {key.usage_stats?.total_requests || 0} requests
                    </div>
                  </div>
                  <div className={`badge badge-${key.status === 'active' ? 'success' : 'warning'}`}>
                    {key.status}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-header">
            <h2>Quick Actions</h2>
            <p>Manage your account</p>
          </div>
          <div className="quick-actions">
            <a href="/api-keys" className="action-item">
              <div className="action-icon">
                <Key size={20} />
              </div>
              <div>
                <h3>Manage API Keys</h3>
                <p>View and manage your API keys</p>
              </div>
            </a>
            <a href="/usage" className="action-item">
              <div className="action-icon">
                <Activity size={20} />
              </div>
              <div>
                <h3>View Usage Stats</h3>
                <p>Track your API usage and performance</p>
              </div>
            </a>
            <a href="/settings" className="action-item">
              <div className="action-icon">
                <Zap size={20} />
              </div>
              <div>
                <h3>Account Settings</h3>
                <p>Update your profile and preferences</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;